// Packages
import Big from 'big.js'
// Helpers
import { CLog } from '../utils/consoleMixin'
import { transformRawInvoice } from '../utils/helpers'
import { transformV2RawInvoice } from '../utils/api-v2/helpers'
import { LaunchDarkly } from '../utils/launchDarkly'
import { LDFlags } from '../utils/constants'

const transformInvoice = (invoice, versionOverride = null) => {
  // Until were 100% in on V2, we have to override transforming create/update/delete invoice API responses
  // when V2 flags are enabled since we're currently just mimicing V1 API calls in V2
  if (
    LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)
    && versionOverride !== 'v1'
  ) {
    return transformV2RawInvoice(invoice)
  }
  else {
    return transformRawInvoice(invoice)
  }
}

const findInvoiceFromId = (invoices, id) => {
  return invoices.find((invoice) => {
    return invoice.id === id
  })
}

const mutations = {
  // If there is a change here, should be a change in DELETE_UNIVERSAL_SEARCH_INVOICE
  DELETE_INVOICE: (state, invoiceID) => {
    if (!invoiceID) {
      console.log('No invoiceID passed to DELETE_INVOICE')
      return
    }

    const invoice = findInvoiceFromId(state.invoices.invoices, invoiceID)
    // invoices.invoices may be empty, and that's alright; we can skip the rest
    if (!invoice) {
      console.log('DELETE_INVOICE: An invoice was not found in state.invoices.invoices')
      return
    }
    const invoiceIndex = state.invoices.invoices.indexOf(invoice)
    state.invoices.invoices.splice(invoiceIndex, 1)
    state.invoices.total -= 1
    // invoice.amounts.total = big instances of pennies
    state.invoices.sum = state.invoices.sum.minus(invoice.amounts.total)
  },

  // If there is a change here, should be a change in DELETE_INVOICE
  DELETE_UNIVERSAL_SEARCH_INVOICE: (state, invoiceID) => {
    if (!invoiceID) {
      console.log('No invoiceID passed to DELETE_INVOICE')
      return
    }

    const invoice = findInvoiceFromId(state.universalSearchResults.invoices, invoiceID)
    // invoices.invoices may be empty, and that's alright; we can skip the rest
    if (!invoice) {
      return
    }
    const invoiceIndex = state.universalSearchResults.invoices.indexOf(invoice)
    state.universalSearchResults.invoices.splice(invoiceIndex, 1)
    state.universalSearchResults.total -= 1
    state.universalSearchResults.sum -= Number.parseFloat((invoice.amounts.total * 100).toFixed(2))
  },

  INSERT_INVOICE: (state, payload) => {
    // payload is the direct response from the BE
    if (!payload || Object.keys(payload).length === 0) {
      return
    }

    /*
     * New invoices created by employees get added to the END of the page's invoices
     * UNLESS the page already has 25 invoices (represented by # of invoices in store)
     *
     * New invoices created by clients get added to the top of the page's invoices
     * REGARDLESS of how many invoices are on the page (should not affect anything at all)
     */
    const transformOverride = payload.transformOverride
    const user = JSON.parse(localStorage.getItem('user'))

    // If it's a client user, add the invoice to the front of the line, regardless
    if (user.client) {
      state.invoices.invoices.unshift(transformInvoice(payload, transformOverride))
    }
    if (user.employee) {
      state.invoices.invoices.push(transformInvoice(payload, transformOverride))
    }
    state.invoices.total = state.invoices.total
      ? state.invoices.total + 1
      : 1
    // invoice.total_amount = pennies returned by BE
    state.invoices.sum = state.invoices.sum.plus(payload.total_amount)
  },

  // If there is a change here, should be a change in REPLACE_EXISTING_UNIVERSAL_SEARCH_INVOICE
  REPLACE_EXISTING_INVOICE: (state, payload) => {
    if (!state.invoices.invoices.length) {
      CLog('Currently, there are no invoices stored in Vuex to look for a match to replace.')
      return
    }
    const invoice = findInvoiceFromId(state.invoices.invoices, payload.id)
    const transformOverride = payload.transformOverride

    if (!invoice) {
      CLog('No matching invoices found.')
      return
    }
    const invoiceIndex = state.invoices.invoices.indexOf(invoice)
    state.invoices.invoices.splice(invoiceIndex, 1, transformInvoice(payload, transformOverride))
  },

  // If there is a change here, should be a change in REPLACE_EXISTING_INVOICE
  REPLACE_EXISTING_UNIVERSAL_SEARCH_INVOICE: (state, payload) => {
    if (!state.universalSearchResults.invoices.length) {
      CLog('Currently, there are no universal search invoices stored in Vuex to look for a match to replace.')
      return
    }
    const invoice = findInvoiceFromId(state.universalSearchResults.invoices, payload.id)
    const transformOverride = payload.transformOverride

    if (!invoice) {
      CLog('No matching universal search invoices found.')
      return
    }
    const invoiceIndex = state.universalSearchResults.invoices.indexOf(invoice)
    state.universalSearchResults.invoices.splice(invoiceIndex, 1, transformInvoice(payload, transformOverride))
  },

  STORE_INVOICE: (state, payload) => {
    const transformOverride = payload.transformOverride
    state.invoice = transformInvoice(payload, transformOverride)
  },

  STORE_INVOICES: (state, payload) => {
    // Format payload into frontend required model
    const count = payload.nbHits
      ? payload.nbHits
      : payload.count
    const invoices = payload.hits || payload.rows || payload.invoices
    // Returned from mapV2Invoices
    const pages = payload.pages
    const searchStats = payload.facets_stats || null
    const transformedInvoices = []

    invoices.forEach((invoice) => {
      transformedInvoices.push(transformInvoice(invoice))
    })
    state.invoices.invoices = transformedInvoices

    if (payload.facets_stats) {
      state.invoices.sum = new Big(payload.facets_stats.total_amount.sum)
    }
    else if (invoices.length) {
      state.invoices.sum = new Big(invoices.reduce((acc, cur) => {
        return acc + cur.total_amount
      }, 0))
    }
    else {
      state.invoices.sum = new Big(0)
    }
    state.invoices.pages = pages
    state.invoices.stats = searchStats
    state.invoices.total = count
  },

  STORE_UNIVERSAL_SEARCH_RESULTS: (state, payload) => {
    // Format payload into frontend required model
    const count = payload.nbHits
      ? payload.nbHits
      : payload.count
    const invoices = payload.hits || payload.rows || payload.invoices
    // Returned from mapV2Invoices
    const pages = payload.pages
    const transformedInvoices = []

    invoices.forEach((invoice) => {
      transformedInvoices.push(transformInvoice(invoice))
    })
    state.universalSearchResults.invoices = transformedInvoices

    if (invoices.length) {
      state.universalSearchResults.sum = invoices.reduce((acc, cur) => {
        return acc + (cur.total_amount / 100)
      }, 0)
    }
    else {
      state.universalSearchResults.sum = false
    }
    state.universalSearchResults.pages = pages
    state.universalSearchResults.total = count
  },
}

export default mutations
